import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, withModifiers as _withModifiers, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "toolbar-section toolbar-section--compact-toggle" }
const _hoisted_2 = ["title"]
const _hoisted_3 = { class: "toolbar-section toolbar-section--tools" }
const _hoisted_4 = {
  key: 0,
  class: "toolbar-label"
}
const _hoisted_5 = { class: "toolbar-buttons-groups" }
const _hoisted_6 = ["title", "onClick"]
const _hoisted_7 = {
  key: 0,
  class: "tool-group-separator"
}
const _hoisted_8 = {
  key: 0,
  class: "toolbar-label"
}
const _hoisted_9 = { class: "toolbar-controls" }
const _hoisted_10 = ["title"]
const _hoisted_11 = { class: "color-picker-panel__header" }
const _hoisted_12 = { class: "color-picker-panel__content" }
const _hoisted_13 = { class: "color-section" }
const _hoisted_14 = { class: "color-section__label" }
const _hoisted_15 = { class: "color-grid" }
const _hoisted_16 = ["title", "onClick"]
const _hoisted_17 = {
  key: 0,
  class: "fas fa-check"
}
const _hoisted_18 = {
  key: 0,
  class: "color-section"
}
const _hoisted_19 = { class: "color-section__label" }
const _hoisted_20 = { class: "color-grid" }
const _hoisted_21 = ["title", "onClick"]
const _hoisted_22 = {
  key: 0,
  class: "fas fa-check"
}
const _hoisted_23 = { class: "color-section" }
const _hoisted_24 = { class: "color-section__label" }
const _hoisted_25 = { class: "custom-color-input" }
const _hoisted_26 = ["value"]
const _hoisted_27 = ["value"]
const _hoisted_28 = { class: "toolbar-section" }
const _hoisted_29 = {
  key: 0,
  class: "toolbar-label"
}
const _hoisted_30 = { class: "toolbar-buttons" }
const _hoisted_31 = ["title", "disabled"]
const _hoisted_32 = ["title", "disabled"]
const _hoisted_33 = { class: "toolbar-section" }
const _hoisted_34 = {
  key: 0,
  class: "toolbar-label"
}
const _hoisted_35 = { class: "toolbar-grid-controls" }
const _hoisted_36 = ["title"]
const _hoisted_37 = {
  key: 0,
  class: "toolbar-controls"
}
const _hoisted_38 = { class: "grid-size-label" }
const _hoisted_39 = ["value"]
const _hoisted_40 = { class: "toolbar-section" }
const _hoisted_41 = {
  key: 0,
  class: "toolbar-label"
}
const _hoisted_42 = { class: "toolbar-buttons" }
const _hoisted_43 = ["title"]

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { AnnotationTool, type AnnotationToolSettings } from '@/types/annotation';

interface Props {
  currentTool: AnnotationTool;
  settings: AnnotationToolSettings;
  canUndo: boolean;
  canRedo: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AnnotationToolbar',
  props: {
    currentTool: {},
    settings: {},
    canUndo: { type: Boolean },
    canRedo: { type: Boolean }
  },
  emits: ["tool-change", "setting-change", "undo", "redo", "clear-all", "calibrate-scale"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props;

const emit = __emit;

const { t } = useI18n();

// Compact mode state
const isCompactMode = ref(false);

// Tool definitions grouped by function
const toolGroups = [
  {
    id: 'selection',
    label: 'Selection',
    tools: [
      { id: AnnotationTool.SELECT, icon: 'fas fa-mouse-pointer' },
    ]
  },
  {
    id: 'drawing',
    label: 'Drawing',
    tools: [
      { id: AnnotationTool.FREEHAND, icon: 'fas fa-pencil-alt' },
      { id: AnnotationTool.RECTANGLE, icon: 'fas fa-square' },
      { id: AnnotationTool.CIRCLE, icon: 'fas fa-circle' },
      { id: AnnotationTool.ARROW, icon: 'fas fa-arrow-right' },
      { id: AnnotationTool.LINE, icon: 'fas fa-minus' },
    ]
  },
  {
    id: 'annotation',
    label: 'Annotation',
    tools: [
      { id: AnnotationTool.TEXT, icon: 'fas fa-font' },
      { id: AnnotationTool.HIGHLIGHT, icon: 'fas fa-highlighter' },
    ]
  },
  {
    id: 'measurement',
    label: 'Measurement',
    tools: [
      { id: AnnotationTool.MEASURE_DISTANCE, icon: 'fas fa-ruler' },
      { id: AnnotationTool.MEASURE_AREA, icon: 'fas fa-draw-polygon' },
      { id: AnnotationTool.MEASURE_POLYGON, icon: 'fas fa-shapes' },
    ]
  },
  {
    id: 'utility',
    label: 'Utility',
    tools: [
      { id: AnnotationTool.ERASER, icon: 'fas fa-eraser' },
    ]
  }
];

// Legacy flat tools array for backward compatibility
const tools = toolGroups.flatMap(group => group.tools);

// Color presets
const colorPresets = [
  '#FF0000', // Red
  '#FF6B00', // Orange
  '#FFFF00', // Yellow
  '#00FF00', // Green
  '#00FFFF', // Cyan
  '#0000FF', // Blue
  '#FF00FF', // Magenta
  '#8B00FF', // Purple
  '#000000', // Black
  '#808080', // Gray
  '#FFFFFF', // White
  '#964B00', // Brown
];

// Color names for tooltips
const colorNames: Record<string, string> = {
  '#FF0000': 'Red',
  '#FF6B00': 'Orange',
  '#FFFF00': 'Yellow',
  '#00FF00': 'Green',
  '#00FFFF': 'Cyan',
  '#0000FF': 'Blue',
  '#FF00FF': 'Magenta',
  '#8B00FF': 'Purple',
  '#000000': 'Black',
  '#808080': 'Gray',
  '#FFFFFF': 'White',
  '#964B00': 'Brown',
};

// Recent colors (stored in ref to track usage)
const recentColors = ref<string[]>([]);

// Color picker dropdown state
const showColorPicker = ref(false);
const colorPickerDropdown = ref<HTMLElement | null>(null);

// Computed dropdown position (for teleported dropdown)
const dropdownPosition = computed(() => {
  if (!colorPickerDropdown.value) {
    console.warn('Dropdown ref not available');
    return {
      position: 'fixed',
      top: '100px',
      left: '100px',
    };
  }
  
  try {
    const rect = colorPickerDropdown.value.getBoundingClientRect();
    const position = {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
    };
    console.log('Dropdown position calculated:', position);
    return position;
  } catch (error) {
    console.error('Error calculating dropdown position:', error);
    return {
      position: 'fixed',
      top: '100px',
      left: '100px',
    };
  }
});

// Get color name
const getColorName = (color: string): string => {
  return colorNames[color.toUpperCase()] || color;
};

// Add color to recent colors
const addToRecentColors = (color: string) => {
  // Remove if already exists
  const index = recentColors.value.indexOf(color);
  if (index > -1) {
    recentColors.value.splice(index, 1);
  }
  // Add to beginning
  recentColors.value.unshift(color);
  // Keep only last 6 colors
  if (recentColors.value.length > 6) {
    recentColors.value.pop();
  }
  // Save to localStorage
  try {
    localStorage.setItem('annotation-recent-colors', JSON.stringify(recentColors.value));
  } catch (e) {
    console.warn('Failed to save recent colors:', e);
  }
};

// Load recent colors from localStorage
const loadRecentColors = () => {
  try {
    const saved = localStorage.getItem('annotation-recent-colors');
    if (saved) {
      recentColors.value = JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load recent colors:', e);
  }
};

// Load compact mode preference
const loadCompactMode = () => {
  try {
    const saved = localStorage.getItem('annotation-compact-mode');
    if (saved !== null) {
      isCompactMode.value = JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Failed to load compact mode:', e);
  }
};

// Toggle compact mode
const toggleCompactMode = () => {
  isCompactMode.value = !isCompactMode.value;
  try {
    localStorage.setItem('annotation-compact-mode', JSON.stringify(isCompactMode.value));
  } catch (e) {
    console.warn('Failed to save compact mode:', e);
  }
};

// Toggle color picker dropdown
const toggleColorPicker = (event?: Event) => {
  if (event) {
    event.stopPropagation();
  }
  showColorPicker.value = !showColorPicker.value;
  console.log('Color picker toggled:', showColorPicker.value);
  console.log('Dropdown element:', colorPickerDropdown.value);
  
  if (showColorPicker.value) {
    nextTick(() => {
      const panel = document.querySelector('.color-picker-panel');
      console.log('Panel found:', panel);
      if (panel) {
        console.log('Panel styles:', window.getComputedStyle(panel).position, window.getComputedStyle(panel).zIndex);
      }
    });
  }
};

// Select a color
const selectColor = (color: string) => {
  addToRecentColors(color);
  emit('setting-change', 'strokeColor', color);
  showColorPicker.value = false;
};

// Handle click outside to close dropdown
const handleClickOutside = (e: MouseEvent) => {
  if (!showColorPicker.value) return;
  
  const target = e.target as Node;
  const panel = document.querySelector('.color-picker-panel');
  
  // Check if click is outside both the toggle button and the panel
  const isOutsideToggle = colorPickerDropdown.value && !colorPickerDropdown.value.contains(target);
  const isOutsidePanel = panel && !panel.contains(target);
  
  if (isOutsideToggle && isOutsidePanel) {
    console.log('Clicked outside, closing picker');
    showColorPicker.value = false;
  }
};

// Initialize
onMounted(() => {
  console.log('AnnotationToolbar mounted');
  console.log('Color picker dropdown ref:', colorPickerDropdown.value);
  loadRecentColors();
  loadCompactMode();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Event handlers
const handleColorChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const color = target.value.toUpperCase();
  addToRecentColors(color);
  emit('setting-change', 'strokeColor', color);
};

const handleColorTextChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let color = target.value.trim();
  
  // Add # if missing
  if (color && !color.startsWith('#')) {
    color = '#' + color;
  }
  
  // Validate hex color format
  if (/^#[0-9A-F]{6}$/i.test(color)) {
    addToRecentColors(color.toUpperCase());
    emit('setting-change', 'strokeColor', color.toUpperCase());
  }
};

const validateColorText = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let color = target.value.trim();
  
  // Add # if missing
  if (color && !color.startsWith('#')) {
    color = '#' + color;
  }
  
  // If invalid, revert to current color
  if (!/^#[0-9A-F]{6}$/i.test(color)) {
    target.value = props.settings.strokeColor;
  }
};

const handleStrokeWidthChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'strokeWidth', parseInt(target.value));
};

const handleFontSizeChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'fontSize', parseInt(target.value));
};

// Get current scale value (the number after "1:")
const getCurrentScaleValue = (): number => {
  const currentScale = props.settings.scale || '1:100';
  const parts = currentScale.split(':');
  return parseFloat(parts[1]) || 100;
};

// Handle custom scale input (real-time updates)
const handleCustomScaleInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = parseFloat(target.value);
  
  if (value && value > 0) {
    const scaleLabel = `1:${value}`;
    emit('setting-change', 'scale', scaleLabel);
    emit('setting-change', 'scaleRatio', value);
  }
};

// Handle custom scale blur/enter (validation)
const handleCustomScaleBlur = (e: Event) => {
  const target = e.target as HTMLInputElement;
  let value = parseFloat(target.value);
  
  // Validate and ensure minimum value
  if (!value || value < 1) {
    value = 100; // Default fallback
    target.value = '100';
  }
  
  const scaleLabel = `1:${value}`;
  emit('setting-change', 'scale', scaleLabel);
  emit('setting-change', 'scaleRatio', value);
};

const handleUnitChange = (e: Event) => {
  const target = e.target as HTMLSelectElement;
  emit('setting-change', 'measurementUnit', target.value);
};

const handleOpacityChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const opacity = parseInt(target.value) / 100;
  emit('setting-change', 'opacity', opacity);
};

const handleGridSizeChange = (e: Event) => {
  const target = e.target as HTMLInputElement;
  emit('setting-change', 'gridSize', parseInt(target.value));
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['annotation-toolbar', { 'annotation-toolbar--compact': isCompactMode.value }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("button", {
        class: _normalizeClass(['toolbar-btn', 'compact-toggle-btn', { active: isCompactMode.value }]),
        title: isCompactMode.value ? _ctx.$t('annotation.expandToolbar') || 'Expand Toolbar' : _ctx.$t('annotation.compactToolbar') || 'Compact Mode',
        onClick: toggleCompactMode
      }, [
        _createElementVNode("i", {
          class: _normalizeClass(isCompactMode.value ? 'fas fa-expand-arrows-alt' : 'fas fa-compress-arrows-alt')
        }, null, 2)
      ], 10, _hoisted_2)
    ]),
    _createElementVNode("div", _hoisted_3, [
      (!isCompactMode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_ctx.$t('annotation.tools')), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(toolGroups, (group, groupIndex) => {
          return _createElementVNode("div", {
            key: group.id,
            class: _normalizeClass(['toolbar-button-group', `toolbar-button-group--${group.id}`])
          }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.tools, (tool) => {
              return (_openBlock(), _createElementBlock("button", {
                key: tool.id,
                class: _normalizeClass(['toolbar-btn', { active: _ctx.currentTool === tool.id }]),
                title: `${_ctx.$t(`annotation.${tool.id}`)} - ${group.label}`,
                onClick: ($event: any) => (_ctx.$emit('tool-change', tool.id))
              }, [
                _createElementVNode("i", {
                  class: _normalizeClass(tool.icon)
                }, null, 2)
              ], 10, _hoisted_6))
            }), 128)),
            (groupIndex < toolGroups.length - 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7))
              : _createCommentVNode("", true)
          ], 2)
        }), 64))
      ])
    ]),
    _createElementVNode("div", {
      class: "toolbar-section color-picker-section",
      ref_key: "colorPickerDropdown",
      ref: colorPickerDropdown
    }, [
      (!isCompactMode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_ctx.$t('annotation.color')), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("button", {
          type: "button",
          class: "color-picker-toggle",
          title: _ctx.$t('annotation.selectColor'),
          onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (toggleColorPicker($event)), ["stop"]))
        }, [
          _createElementVNode("span", {
            class: "color-picker-toggle__color",
            style: _normalizeStyle({ backgroundColor: _ctx.settings.strokeColor })
          }, null, 4),
          _cache[6] || (_cache[6] = _createElementVNode("i", { class: "fas fa-palette" }, null, -1))
        ], 8, _hoisted_10)
      ]),
      (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
        (showColorPicker.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "color-picker-panel annotation-color-picker-panel",
              style: _normalizeStyle(dropdownPosition.value),
              "data-testid": "color-picker-panel"
            }, [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("span", null, _toDisplayString(_ctx.$t('annotation.selectColor') || 'Select Color'), 1),
                _createElementVNode("button", {
                  class: "btn-close-picker",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (showColorPicker.value = false)),
                  title: "Close"
                }, [...(_cache[7] || (_cache[7] = [
                  _createElementVNode("i", { class: "fas fa-times" }, null, -1)
                ]))])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, _toDisplayString(_ctx.$t('annotation.presetColors') || 'Preset Colors'), 1),
                  _createElementVNode("div", _hoisted_15, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(colorPresets, (color) => {
                      return _createElementVNode("button", {
                        key: color,
                        class: _normalizeClass(['color-swatch', { active: _ctx.settings.strokeColor === color }]),
                        style: _normalizeStyle({ backgroundColor: color }),
                        title: getColorName(color),
                        onClick: ($event: any) => (selectColor(color))
                      }, [
                        (_ctx.settings.strokeColor === color)
                          ? (_openBlock(), _createElementBlock("i", _hoisted_17))
                          : _createCommentVNode("", true)
                      ], 14, _hoisted_16)
                    }), 64))
                  ])
                ]),
                (recentColors.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                      _createElementVNode("div", _hoisted_19, _toDisplayString(_ctx.$t('annotation.recentColors') || 'Recent Colors'), 1),
                      _createElementVNode("div", _hoisted_20, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(recentColors.value, (color, index) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: `recent-${index}`,
                            class: _normalizeClass(['color-swatch', { active: _ctx.settings.strokeColor === color }]),
                            style: _normalizeStyle({ backgroundColor: color }),
                            title: color,
                            onClick: ($event: any) => (selectColor(color))
                          }, [
                            (_ctx.settings.strokeColor === color)
                              ? (_openBlock(), _createElementBlock("i", _hoisted_22))
                              : _createCommentVNode("", true)
                          ], 14, _hoisted_21))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_23, [
                  _createElementVNode("div", _hoisted_24, _toDisplayString(_ctx.$t('annotation.customColor') || 'Custom Color'), 1),
                  _createElementVNode("div", _hoisted_25, [
                    _createElementVNode("input", {
                      type: "color",
                      value: _ctx.settings.strokeColor,
                      onInput: handleColorChange,
                      class: "color-input-native"
                    }, null, 40, _hoisted_26),
                    _createElementVNode("input", {
                      type: "text",
                      value: _ctx.settings.strokeColor,
                      onInput: handleColorTextChange,
                      onBlur: validateColorText,
                      class: "color-input-text",
                      placeholder: "#000000",
                      maxlength: "7"
                    }, null, 40, _hoisted_27)
                  ])
                ])
              ])
            ], 4))
          : _createCommentVNode("", true)
      ]))
    ], 512),
    _createElementVNode("div", _hoisted_28, [
      (!isCompactMode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_29, _toDisplayString(_ctx.$t('annotation.history')), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_30, [
        _createElementVNode("button", {
          class: _normalizeClass(['toolbar-btn', { disabled: !_ctx.canUndo }]),
          title: _ctx.$t('common.undo') + ' (Ctrl+Z)',
          onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('undo'))),
          disabled: !_ctx.canUndo
        }, [...(_cache[8] || (_cache[8] = [
          _createElementVNode("i", { class: "fas fa-undo" }, null, -1)
        ]))], 10, _hoisted_31),
        _createElementVNode("button", {
          class: _normalizeClass(['toolbar-btn', { disabled: !_ctx.canRedo }]),
          title: _ctx.$t('common.redo') + ' (Ctrl+Y)',
          onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('redo'))),
          disabled: !_ctx.canRedo
        }, [...(_cache[9] || (_cache[9] = [
          _createElementVNode("i", { class: "fas fa-redo" }, null, -1)
        ]))], 10, _hoisted_32)
      ])
    ]),
    _createElementVNode("div", _hoisted_33, [
      (!isCompactMode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_34, _toDisplayString(_ctx.$t('annotation.grid')), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_35, [
        _createElementVNode("button", {
          class: _normalizeClass(['toolbar-btn', { active: _ctx.settings.showGrid }]),
          title: _ctx.$t('annotation.toggleGrid'),
          onClick: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('setting-change', 'showGrid', !_ctx.settings.showGrid)))
        }, [...(_cache[10] || (_cache[10] = [
          _createElementVNode("i", { class: "fas fa-th" }, null, -1)
        ]))], 10, _hoisted_36),
        (_ctx.settings.showGrid)
          ? (_openBlock(), _createElementBlock("div", _hoisted_37, [
              _createElementVNode("label", _hoisted_38, _toDisplayString(_ctx.$t('annotation.gridSize')) + ":", 1),
              _createElementVNode("input", {
                type: "number",
                min: "10",
                max: "100",
                step: "5",
                value: _ctx.settings.gridSize || 20,
                onInput: handleGridSizeChange,
                class: "number-input number-input--small"
              }, null, 40, _hoisted_39),
              _cache[11] || (_cache[11] = _createElementVNode("span", { class: "value-display" }, "px", -1))
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_40, [
      (!isCompactMode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_41, _toDisplayString(_ctx.$t('annotation.clearAll')), 1))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_42, [
        _createElementVNode("button", {
          class: "toolbar-btn toolbar-btn--danger",
          title: _ctx.$t('annotation.clearAll'),
          onClick: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('clear-all')))
        }, [...(_cache[12] || (_cache[12] = [
          _createElementVNode("i", { class: "fas fa-trash" }, null, -1)
        ]))], 8, _hoisted_43)
      ])
    ])
  ], 2))
}
}

})